"use client";

import { TContactConfig } from "@/app/config";
import { Rocket, Eye, StarsIcon } from "lucide-react";
import Link from "next/link";
import React from "react";
import { motion } from "motion/react";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};


export default function ContactHeroSection({
  config,
}: {
  config: TContactConfig["hero"];
}) {
  return (
    <section className="min-h-screen bg-white relative overflow-hidden">
      {/* Animated Background gradient circles */}
      <motion.div
        className="absolute top-20 left-0 w-96 h-96 bg-gradient-to-br from-yellow-200 to-orange-300 rounded-full opacity-30 blur-3xl"
        variants={{
          hidden: { scale: 0, opacity: 0 },
          visible: {
            scale: 1,
            opacity: 0.3,
            transition: {
              duration: 2,
              ease: "easeOut",
            },
          },
        }}
        initial="hidden"
        animate={{
          y: [-10, 10, -10],
          rotate: [-5, 5, -5],
          transition: {
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          },
        }
      }
        transition={{ delay: 0.5 }}
       
      />
      <motion.div
        className="absolute bottom-20 right-0 w-80 h-80 bg-gradient-to-br from-orange-200 to-yellow-300 rounded-full opacity-25 blur-3xl"
        variants={{
          hidden: { scale: 0, opacity: 0 },
          visible: {
            scale: 1,
            opacity: 0.3,
            transition: {
              duration: 2,
              ease: "easeOut",
            },
          },
        }}
        initial="hidden"
        
        transition={{ delay: 0.5 }}
        animate={{
          y: [10, -20, 10],
          x: [-5, 5, -5],
          transition: {
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          },
        }}
      />
      <motion.div
        className="absolute top-1/2 right-10 w-64 h-64 bg-gradient-to-br from-yellow-300 to-orange-200 rounded-full opacity-20 blur-2xl"
        variants={{
          hidden: { scale: 0, opacity: 0 },
          visible: {
            scale: 1,
            opacity: 0.3,
            transition: {
              duration: 2,
              ease: "easeOut",
            },
          },
        }}
        initial="hidden"
      
        transition={{ delay: 1 }}
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 180, 360],
          transition: {
            duration: 10,
            repeat: Infinity,
            ease: "linear",
          },
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-yellow-300 rounded-full opacity-40"
            initial={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
            }}
            animate={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "linear",
            }}
          />
        ))}
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-16 lg:py-24 relative z-10">
        <motion.div
          className="text-center space-y-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-gray-400/60 gap-2.5"
            variants={{
              hidden: { scale: 0, opacity: 0, y: -20 },
              visible: {
                scale: 1,
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeInOut",
                },
              },
              hover: {
                scale: 1.05,
                y: -2,
                transition: {
                  duration: 0.2,
                  ease: "easeInOut",
                },
              },
            }}
            whileHover="hover"
            whileTap={{ scale: 0.95 }}
          >
            <motion.div variants={{
              hover: {
                rotate: 10,
                scale: 1.1,
                transition: {
                  duration: 0.2,
                  ease: "easeInOut",
                },
              },
            }} whileHover="hover">
              <StarsIcon className="w-5 h-5 text-yellow-500" />
            </motion.div>
            <span className="text-base font-extralight">{config.tag}</span>
          </motion.div>

          {/* Main Headlines */}
          <div className="space-y-4">
            <motion.h1
              className="text-5xl lg:text-7xl font-bold text-gray-800 leading-tight"
              variants={{
                hidden: { opacity: 0, y: 50, scale: 0.9 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.8,
                    ease: "easeOut",
                  },
                },
              }}
            >
              {config.title}
            </motion.h1>
            
            <motion.h2
              className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent leading-tight"
              variants={{
                hidden: { opacity: 0, y: 50, scale: 0.8 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.9,
                    ease: "easeOut",
                    delay: 0.1,
                  },
                },
              }}
              whileHover={{
                scale: 1.05,
                textShadow: "0px 0px 8px rgba(245, 158, 11, 0.5)",
                transition: { duration: 0.2 },
              }}
            >
              {config.highlighted_title}
            </motion.h2>
            
            <motion.h3
              className="text-4xl lg:text-6xl font-bold text-gray-800 leading-tight"
              variants={{
                hidden: { opacity: 0, y: 50, scale: 0.9 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.8,
                    ease: "easeOut",
                    delay: 0.2,
                  },
                },
              }}
            >
              {config.sub_title}
            </motion.h3>
          </div>

          {/* Description */}
          <motion.p
            className="text-gray-600 text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto"
            variants={ {
              hidden: { opacity: 0, y: 30 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.7,
                  ease: "easeOut",
                },
              },
            }}
          >
            {config.description}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: "easeOut",
                  staggerChildren: 0.1,
                },
              },
            }}
          >
            <Link href={config.Button.href} target="_blank">
              <motion.button
                className="bg-yellow-400 hover:bg-yellow-500 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-3 shadow-lg hover:shadow-xl"
                variants={{
                  hidden: { opacity: 0, scale: 0.8, y: 20 },
                  visible: {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: "easeInOut",
                    },
                  },
                  hover: {
                    scale: 1.05,
                    y: -3,
                    transition: {
                      duration: 0.2,
                      ease: "easeInOut",
                    },
                  },
                  tap: {
                    scale: 0.98,
                  },
                }}
                whileHover="hover"
                whileTap="tap"
              >
                <motion.div variants={{
                  hover: {
                    rotate: 10,
                    scale: 1.1,
                    transition: {
                      duration: 0.2,
                      ease: "easeInOut",
                    },
                  },
                }} whileHover="hover">
                  <Rocket className="w-5 h-5 fill-current" />
                </motion.div>
                <span>{config.Button.title}</span>
              </motion.button>
            </Link>
            
            <Link href={config.Button_2.href} target="_blank">
              <motion.button
                className="bg-transparent hover:bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-3 border border-gray-300 hover:border-gray-400"
                variants={ {
                  hidden: { opacity: 0, scale: 0.8, y: 20 },
                  visible: {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: "easeInOut",
                    },
                  },
                  hover: {
                    scale: 1.03,
                    y: -2,
                    transition: {
                      duration: 0.2,
                      ease: "easeInOut",
                    },
                  },
                  tap: {
                    scale: 0.98,
                  },
                }}
                whileHover="hover"
                whileTap="tap"
              >
                <motion.div variants={{
  hover: {
    rotate: 10,
    scale: 1.1,
    transition: {
      duration: 0.2,
      ease: "easeInOut",
    },
  },
}} whileHover="hover">
                  <Eye className="w-5 h-5" />
                </motion.div>
                <span>{config.Button_2.title}</span>
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Animated decorative elements */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-4 h-4 bg-yellow-400 rounded-full opacity-60"
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.6, 0.2, 0.6],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      <motion.div
        className="absolute top-3/4 right-1/3 w-3 h-3 bg-orange-400 rounded-full opacity-50"
        animate={{
          scale: [1, 2, 1],
          opacity: [0.5, 0.1, 0.5],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      />
    </section>
  );
}